//
// Copyright 2024 DXOS.org
//

import React from 'react';

import { Obj, Type } from '@dxos/echo';
import { Doc } from '@dxos/echo-doc';
import { IconButton, Input, ScrollArea, useThemeContext } from '@dxos/react-ui';
import { composable, composableProps } from '@dxos/react-ui';
import { useTextEditor } from '@dxos/react-ui-editor';
import { mapSchemaToFields } from '@dxos/schema';
import { automerge, createBasicExtensions, createMarkdownExtensions, createThemeExtensions } from '@dxos/ui-editor';

const MAX_RENDERED_COUNT = 80;

export type ItemListProps<T> = { objects: T[] } & Pick<ItemProps<T>, 'debug' | 'onDelete'>;

export const ItemList = composable<HTMLDivElement, ItemListProps<Obj.Any>>(
  ({ objects, debug, onDelete, ...props }, forwardedRef) => {
    return (
      <ScrollArea.Root {...composableProps(props)} padding ref={forwardedRef}>
        <ScrollArea.Viewport>
          {objects
            .slice(0, MAX_RENDERED_COUNT)
            .map(
              (object) =>
                (debug && <DebugItem key={object.id} object={object} onDelete={onDelete} />) || (
                  <Item key={object.id} object={object} onDelete={onDelete} />
                ),
            )}
          {objects.length > MAX_RENDERED_COUNT && (
            <div className='text-xs text-gray-400'>({objects.length - MAX_RENDERED_COUNT} more items)</div>
          )}
        </ScrollArea.Viewport>
      </ScrollArea.Root>
    );
  },
);

ItemList.displayName = 'ItemList';

const labelProps = 'shrink-0 w-20 text-right text-primary-500 px-2 py-[2px]';

export type ItemProps<T> = {
  object: T;
  debug?: boolean;
  onDelete: (id: string) => void;
};

// TODO(burdon): Use ui list with key nav/selection.
// TODO(burdon): Toggle options to show deleted.
export const Item = ({ object, onDelete }: ItemProps<Obj.Any>) => {
  const type = Obj.getType(object);
  if (!type) {
    return <DebugItem object={object} onDelete={onDelete} />;
  }

  // TODO(burdon): Get additional metadata.
  const props = mapSchemaToFields(Type.getSchema(type));

  // TODO(burdon): [API]: Type check?
  const getValue = (object: Obj.Any, prop: string) => object[prop];
  const setValue = (object: Obj.Any, prop: string, value: any) =>
    Obj.update(object, (object) => (object[prop] = value));

  return (
    <div className='flex m-1 p-2 border dx-hover'>
      <div className='flex flex-col grow overflow-hidden gap-2'>
        {props.map(({ property, type }) => (
          <div key={property} className='flex'>
            {/* TODO(burdon): Check if editable or meta prop (e.g., id). */}
            {property === 'id' && (
              <Input.Root>
                <Input.Label classNames={labelProps}>{property}</Input.Label>
                <div className='font-mono text-xs py-1'>{getValue(object, property).slice(0, 8)}</div>
              </Input.Root>
            )}
            {type === 'boolean' && (
              <Input.Root>
                <Input.Label classNames={labelProps}>{property}</Input.Label>
                <Input.Checkbox
                  checked={(object as any)[property]}
                  onCheckedChange={(state) => setValue(object, property, !!state)}
                />
              </Input.Root>
            )}
            {property !== 'id' && type === 'string' && (
              <Input.Root>
                <Input.Label classNames={labelProps}>{property}</Input.Label>
                <Editor object={object} prop={property} />
              </Input.Root>
            )}
          </div>
        ))}
      </div>

      {/* TODO(burdon): Check if mutable. */}
      <div className='flex flex-col shrink-0'>
        <IconButton icon='ph--x--regular' iconOnly label='Delete' onClick={() => onDelete(object.id)} variant='ghost' />
      </div>
    </div>
  );
};

const Editor = ({ object, prop }: { object: Obj.Any; prop: string }) => {
  const { themeMode } = useThemeContext();
  const { parentRef } = useTextEditor(() => {
    return {
      initialValue: object[prop],
      extensions: [
        createBasicExtensions(),
        createMarkdownExtensions(),
        createThemeExtensions({ themeMode, slots: { content: { className: 'p-0' } } }),
        automerge(Doc.createAccessor(object, [prop])),
      ],
    };
  }, []);

  return <div ref={parentRef} className='grow' />;
};

// TODO(burdon): Add metadata.
export const DebugItem = ({ object, onDelete }: Pick<ItemProps<Obj.Any>, 'object' | 'onDelete'>) => {
  const meta = Obj.getMeta(object);
  const deleted = JSON.stringify(object).indexOf('@deleted') !== -1; // TODO(burdon): [API] Missing API.
  return (
    <div className='flex w-full px-1.5 py-1 text-sm font-thin font-mono'>
      <pre className='grow'>{JSON.stringify({ id: object.id.slice(0, 8), deleted, ...meta }, undefined, 2)}</pre>
      <IconButton icon='ph--x--regular' variant='ghost' iconOnly onClick={() => onDelete(object.id)} label='Delete' />
    </div>
  );
};
